import { useState } from "react";
import { CalendarClock, Info } from "lucide-react";
import { userReportsApi, updateProfile } from "../../api";
import { useAuth } from "../../hooks/useAuth";
import { Button, ChipInput, Modal, SchedulePicker } from "../ui";
import { describeCron, isValidCron } from "../../utils";
import type { UserReport } from "../../types/reports";

const DEFAULT_CRON = "0 8 * * *";

type ScheduleModalProps = {
  report: UserReport;
  onClose: () => void;
  onSuccess: () => void;
};

export default function ScheduleModal({ report, onClose, onSuccess }: ScheduleModalProps) {
  const { user, refreshUser } = useAuth();
  const existing = report.schedule;
  const hasSlackBot = Boolean(user?.permissions?.slack_bot);

  const [cron, setCron] = useState(existing?.cron_expression || DEFAULT_CRON);
  const [deliverEmail, setDeliverEmail] = useState(existing?.deliver_email ?? true);
  const [deliverSlack, setDeliverSlack] = useState(existing?.deliver_slack ?? false);
  const [recipientEmails, setRecipientEmails] = useState<string[]>(existing?.recipient_emails ?? []);
  const [slackTarget, setSlackTarget] = useState<"dm" | "channel">(
    existing?.slack_channel_ids?.length ? "channel" : "dm",
  );
  const [slackChannelIds, setSlackChannelIds] = useState<string[]>(existing?.slack_channel_ids ?? []);
  const [slackId, setSlackId] = useState(user?.slack_user_id ?? "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const needsSlackId = deliverSlack && slackTarget === "dm" && !user?.slack_user_id;
  const needsChannelId = deliverSlack && slackTarget === "channel";

  const channels: string[] = [];
  if (deliverEmail) {
    const extra = recipientEmails.length
      ? ` and ${recipientEmails.length} other ${recipientEmails.length === 1 ? "address" : "addresses"}`
      : "";
    channels.push(`emailed to you${user?.email ? ` (${user.email})` : ""}${extra}`);
  }
  if (deliverSlack) {
    channels.push(
      slackTarget === "channel"
        ? `posted to ${slackChannelIds.length || "a"} Slack ${slackChannelIds.length === 1 ? "channel" : "channels"}`
        : "sent to you on Slack",
    );
  }
  const outcome = channels.length
    ? `This report will be ${channels.join(" and ")}, on the schedule above.`
    : null;

  async function save() {
    if (!cron.trim()) return setError("Choose when the report should run");
    if (!isValidCron(cron)) return setError("That schedule isn't a valid cron expression");
    if (!deliverEmail && !deliverSlack) return setError("Select at least one delivery channel");
    if (needsSlackId && !slackId.trim()) return setError("Enter your Slack member ID to receive Slack DMs");
    if (needsChannelId && slackChannelIds.length === 0) return setError("Add at least one Slack channel ID");
    setLoading(true);
    setError("");
    try {
      if (needsSlackId) {
        await updateProfile({ slack_user_id: slackId.trim() });
        await refreshUser();
      }
      await userReportsApi.upsertSchedule(report.id, {
        label: describeCron(cron) ?? cron,
        cron_expression: cron,
        is_active: true,
        deliver_email: deliverEmail,
        deliver_slack: deliverSlack,
        recipient_emails: deliverEmail ? recipientEmails : [],
        slack_channel_ids: needsChannelId ? slackChannelIds : [],
      });
      onSuccess();
      onClose();
    } catch {
      setError("");
    } finally {
      setLoading(false);
    }
  }

  async function remove() {
    await userReportsApi.deleteSchedule(report.id);
    onSuccess();
    onClose();
  }

  return (
    <Modal
      onClose={onClose}
      title="Schedule report"
      subtitle={report.name}
      footer={
        <>
          {existing && (
            <button type="button" onClick={remove} className="mr-auto text-sm text-red-600 hover:text-red-700">
              Remove schedule
            </button>
          )}
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={loading}>
            {loading ? "Saving..." : existing ? "Update schedule" : "Turn on schedule"}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <SchedulePicker value={existing?.cron_expression || DEFAULT_CRON} onChange={setCron} />

        <div className="border-t border-gray-200 pt-4">
          <label className="mb-2 block text-xs font-semibold text-gray-700">Where should it go?</label>
          <div className="space-y-2">
            <label className="flex cursor-pointer items-center gap-3 text-sm text-gray-800">
              <input type="checkbox" checked={deliverEmail} onChange={(e) => setDeliverEmail(e.target.checked)} />
              Email me{user?.email ? ` (${user.email})` : ""}
            </label>
            {hasSlackBot && (
              <label className="flex cursor-pointer items-center gap-3 text-sm text-gray-800">
                <input type="checkbox" checked={deliverSlack} onChange={(e) => setDeliverSlack(e.target.checked)} />
                Send to Slack
              </label>
            )}
          </div>

          {deliverEmail && (
            <div className="mt-3">
              <label className="mb-1 block text-xs font-medium text-gray-600">Also send to (optional)</label>
              <ChipInput
                values={recipientEmails}
                onChange={setRecipientEmails}
                placeholder="Add an email address and press Enter"
                validate={(v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)}
              />
              <p className="mt-1 text-xs text-gray-400">
                Anyone added here gets the report by email — they don&apos;t need a Brian account.
              </p>
            </div>
          )}

          {hasSlackBot && deliverSlack && (
            <div className="mt-3 space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <label className="flex cursor-pointer items-center gap-3 text-sm text-gray-800">
                <input type="radio" name="slack-target" checked={slackTarget === "dm"} onChange={() => setSlackTarget("dm")} />
                Direct message each recipient
              </label>
              <label className="flex cursor-pointer items-center gap-3 text-sm text-gray-800">
                <input type="radio" name="slack-target" checked={slackTarget === "channel"} onChange={() => setSlackTarget("channel")} />
                Post to a Slack channel
              </label>
              {slackTarget === "channel" && (
                <div>
                  <ChipInput
                    values={slackChannelIds}
                    onChange={setSlackChannelIds}
                    placeholder="Add a channel ID (e.g. C0123456789) and press Enter"
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    Brian must be invited to each channel. Find the channel ID in Slack under the channel name &rarr; About &rarr; Copy channel ID.
                  </p>
                </div>
              )}
            </div>
          )}

          {needsSlackId && (
            <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <label className="mb-1 block text-sm font-medium text-gray-700">Your Slack member ID</label>
              <input
                value={slackId}
                onChange={(e) => setSlackId(e.target.value)}
                placeholder="e.g. U0123456789"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-400">
                Link your Slack account so Brian can DM you. Find your member ID in Slack under your profile &rarr; More &rarr; Copy member ID. You can also set this in Settings &rarr; Profile.
              </p>
            </div>
          )}
        </div>

        <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2.5">
          <div className="flex items-start gap-2">
            <CalendarClock size={15} className="mt-0.5 shrink-0 text-[#1D345F]" aria-hidden="true" />
            <p className="text-sm text-gray-800">
              {outcome ?? "Pick a delivery channel above to turn this on."}
            </p>
          </div>
          <p className="mt-1.5 flex items-center gap-1.5 pl-[23px] text-xs text-gray-500">
            <Info size={11} aria-hidden="true" />
            Times are in UTC. Nothing is sent until you turn it on, and you can change or stop it anytime.
          </p>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}
